import React, { useRef, useEffect, useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';

import { darkTheme, lightTheme } from '../themes';
import { formatNumber } from '@/utils/formatters';
import useResponsiveChartHeight from '@/components/highcharts/powerAnalyser/hooks/useResponsiveChartHeight';

/**
 * formatTimestamp
 * Returns a Danish “weekday day month” label (e.g. “tir 25 dec”)
 * The input can be a Date, a millisecond timestamp, or an ISO string.
 */
const formatTimestamp = (ts) =>
  new Date(ts).toLocaleDateString('da-DK', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });

const PowerLoadProfilesAnalysisChart = ({
  loadData,
  isLoading = false,
  powerData,
}) => {
  const chartHeight = useResponsiveChartHeight();

  const chartRef = useRef(null);
  const { palette } = useTheme();
  const themeOptions = palette.mode === 'dark' ? darkTheme : lightTheme;
  const { t } = useTranslation('energyxplorer-analysis-tabs');

  /* -------- series + categories -------- */
  /* ---------- series + x-axis values (differential) ---------- */
  const { chartSeries, xMin, xMax } = useMemo(() => {
    if (!loadData?.length) return { chartSeries: [] };
    if (!powerData?.length) return { chartSeries: [] };

    const toMs = (ts) => new Date(ts).getTime();
    const categories = powerData.map((d) => toMs(d.timestamp));

    // helper to map an array of point values into [x, y] pairs
    const pair = (values) => values.map((y, i) => [toMs(y.timestamp), y.value]);

    return {
      chartSeries: [
        {
          name: t('power_load_profiles.consumption'),
          type: 'area',
          data: pair(powerData),
          color: 'hsla(0, 2.00%, 48.40%, 0.45)',
          tooltip: { valueDecimals: 1 },
        },
        {
          name: t('power_load_profiles.peak'),
          type: 'area',
          data: pair(
            loadData.map((d) => ({
              timestamp: d.timestamp,
              value: d.peak_load,
            })),
          ),
          color: 'hsla(0, 75%, 60%, 0.2)',
          tooltip: { valueDecimals: 1 },
        },
        {
          name: t('power_load_profiles.intermediate'),
          type: 'area',
          data: pair(
            loadData.map((d) => ({
              timestamp: d.timestamp,
              value: d.intermediate_load,
            })),
          ),
          color: 'rgba(244, 192, 37, 0.35)',
          tooltip: { valueDecimals: 1 },
        },

        {
          name: t('power_load_profiles.base'),
          type: 'area',
          data: pair(
            loadData.map((d) => ({
              timestamp: d.timestamp,
              value: d.base_load,
            })),
          ),
          color: 'hsla(120, 60%, 45%, 0.5)',
          tooltip: { valueDecimals: 1 },
        },
      ],
      xMin: categories.at(0),
      xMax: categories.at(-1),
    };
  }, [loadData, powerData]);

  /* -------- options -------- */

  const options = useMemo(
    () => ({
      chart: {
        type: 'area',
        zooming: {
          type: 'x',
        },
        spacingLeft: 40,
        spacingRight: 40,
        height: chartHeight,
        backgroundColor: themeOptions.chart.backgroundColor,
        events: {
          load() {
            if (isLoading) this.showLoading('Loading data…');
          },
        },
      },
      time: { useUTC: false },
      credits: { enabled: false },
      title: { text: '' },
      xAxis: {
        type: 'datetime',
        min: xMin,
        max: xMax,
        crosshair: { width: 1, color: 'gray', dashStyle: 'shortdot' },
        labels: {
          formatter() {
            return formatTimestamp(this.value);
          },
          style: { color: themeOptions.xAxis.labels.style.color },
        },
        lineColor: themeOptions.xAxis.lineColor,
      },
      yAxis: {
        min: 0,
        title: {
          text: 'Last (kW)',
          style: { color: themeOptions.yAxis.title.style.color },
        },
        labels: { style: { color: themeOptions.yAxis.labels.style.color } },
        stackLabels: {
          enabled: true,
          formatter() {
            return formatNumber(this.total);
          },
        },
      },
      tooltip: {
        shared: true,
        useHTML: true,
        backgroundColor: themeOptions.tooltip.backgroundColor,
        borderColor: themeOptions.tooltip.borderColor,
        style: {
          color: palette.mode === 'dark' ? 'white' : 'black',
        },
        formatter() {
          return (
            this.points
              .map(
                (p) =>
                  `<span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${p.color};margin-right:5px;"></span>` +
                  `${p.series.name}: <b>${formatNumber(p.y)} kW</b>`,
              )
              .join('<br/>') +
            `<br/><span style="font-size:12px;color:gray;">${formatTimestamp(
              this.x,
            )}</span>`
          );
        },
      },
      legend: { itemStyle: { color: themeOptions.legend.itemStyle.color } },

      series: chartSeries,
    }),
    [chartSeries, themeOptions, isLoading, xMin, xMax],
  );

  /* -------- loading indicator -------- */
  useEffect(() => {
    const chart = chartRef.current?.chart;
    if (!chart) return;
    isLoading ? chart.showLoading('Loading data…') : chart.hideLoading();
  }, [isLoading]);

  return (
    <HighchartsReact highcharts={Highcharts} options={options} ref={chartRef} />
  );
};

export default PowerLoadProfilesAnalysisChart;
